import React, { useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TablePagination,
    TableRow,
    Typography
} from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { convertLocalDateString } from 'components/Common/Utils/DateUtils';
import { SHARING_REQUEST_STATUS, titleStatus } from './sharingRequestModel';
import StatusChip from './StatusChip';
import styles from './VisibilitySharing.module.css';

function showDate(value) {
    if (!value) {
        return '';
    }
    try {
        return convertLocalDateString(value);
    } catch (error) {
        return '';
    }
}

function eventTypes(groupItems) {
    const events = (groupItems || [])
        .filter((item) => item?.event_types?.length)
        .map((item) =>
            item.event_types
                .map((event) => event?.name)
                .filter(Boolean)
                .join(', ')
        )
        .filter(Boolean);
    return events.length > 0 ? events.join(', ') : 'No events';
}

const V1SharingGroups = ({ groups, parentPending, onDecide }) => {
    const entries = Object.entries(groups || {});
    const [pages, setPages] = useState({});

    if (entries.length === 0) {
        return <Typography>No sharing groups found.</Typography>;
    }

    return entries.map(([groupName, groupItems]) => {
        const items = Array.isArray(groupItems) ? groupItems : [];
        const pageState = pages[groupName] || { page: 0, rowsPerPage: 5 };
        const start = pageState.page * pageState.rowsPerPage;
        const visible = items.slice(start, start + pageState.rowsPerPage);
        const groupStatus = titleStatus(items[0]?.request_status) || '';
        const isMob = items[0]?.agreement_type === 'MOB';
        const showAccept = !parentPending && groupStatus === SHARING_REQUEST_STATUS.PENDING;
        const showDecline =
            !parentPending &&
            (groupStatus === SHARING_REQUEST_STATUS.PENDING ||
                groupStatus === SHARING_REQUEST_STATUS.ACCEPTED);

        return (
            <Accordion key={groupName} square>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <div className={styles.groupHeader}>
                        <div className={styles.groupIdentity}>
                            <Typography className={styles.groupName}>{groupName}</Typography>
                            {groupStatus ? <StatusChip status={groupStatus} /> : null}
                        </div>
                        {showAccept || showDecline ? (
                            <div className={styles.actions}>
                                {showAccept ? (
                                    <Button
                                        size="small"
                                        color="primary"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onDecide({ groupName }, 'accept');
                                        }}
                                    >
                                        Accept
                                    </Button>
                                ) : null}
                                {showDecline ? (
                                    <Button
                                        size="small"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            onDecide({ groupName }, 'decline');
                                        }}
                                    >
                                        Decline
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                </AccordionSummary>
                <AccordionDetails>
                    <div className={styles.groupBody}>
                        <Typography>
                            {isMob ? 'GPS' : 'TMS'}
                            {isMob ? '' : ` · ${eventTypes(items)}`}
                        </Typography>
                        <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
                            {isMob ? 'Vehicles' : 'Shippers'}
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {isMob ? (
                                        <>
                                            <TableCell>Truck ID</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Tracking Start</TableCell>
                                            <TableCell>Tracking End</TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell>Shipper Name</TableCell>
                                            <TableCell>Shipper Code</TableCell>
                                            <TableCell>Shipment Identifiers</TableCell>
                                            <TableCell>Application Name</TableCell>
                                        </>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visible.map((item) => (
                                    <TableRow
                                        key={
                                            item.id ||
                                            `${groupName}-${item.truck_number}-${item.shipper_code}`
                                        }
                                    >
                                        {isMob ? (
                                            <>
                                                <TableCell>{item.truck_number}</TableCell>
                                                <TableCell>
                                                    {item.status ? 'ACTIVE' : 'INACTIVE'}
                                                </TableCell>
                                                <TableCell>{showDate(item.enabled_from)}</TableCell>
                                                <TableCell>{showDate(item.expires_on)}</TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>{item.name}</TableCell>
                                                <TableCell>{item.shipper_code}</TableCell>
                                                <TableCell>
                                                    {Array.isArray(item.shipment_identifier)
                                                        ? item.shipment_identifier.join(', ')
                                                        : ''}
                                                </TableCell>
                                                <TableCell>
                                                    {[item.datasource?.name, item.datasource?.type]
                                                        .filter(Boolean)
                                                        .join(' - ')}
                                                </TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            count={items.length}
                            page={pageState.page}
                            onChangePage={(event, nextPage) =>
                                setPages((current) => ({
                                    ...current,
                                    [groupName]: { ...pageState, page: nextPage }
                                }))
                            }
                            rowsPerPage={pageState.rowsPerPage}
                            onChangeRowsPerPage={(event) =>
                                setPages((current) => ({
                                    ...current,
                                    [groupName]: {
                                        page: 0,
                                        rowsPerPage: parseInt(event.target.value, 10)
                                    }
                                }))
                            }
                            rowsPerPageOptions={[5, 10, 20]}
                        />
                    </div>
                </AccordionDetails>
            </Accordion>
        );
    });
};

export default V1SharingGroups;
